import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { Color, Hsv } from 'culori/fn';
import { formatHex } from 'culori/fn';
import { convertToHsv } from './color-conversion-utils';
import { transitionBetweenColors } from './transitionBetweenColors';
import { calculateContrast, getAccessibleColor, hasAccessibleDerivatives } from './contrast-utils';
import type { ColorPickerProps } from './ColorPicker';

export type ColorPickerContextValue = Pick<
  ColorPickerProviderProps,
  'colorForComparison' | 'minimumContrastRatio' | 'opacityForContrastCalculation'
> & {
  currentContrastRatio: number;
  getAccessibleDerivativeColor: (originalColor: Color | string) => Hsv;
  hasAccessibleDerivatives: boolean;
  isDragging: boolean;
  isTransitioning: boolean;
  nonDerivedValueAsHex: string;
  nonDerivedValueAsHsv: Hsv;
  onChangeNonDerivedValueAsHsv: (nonDerivedValue: Hsv) => void;
  shouldEnforceMinContrast: boolean;
  setIsDragging: (isDragging: boolean) => void;
  setShouldEnforceMinContrast: (shouldEnforceMinContrast: boolean) => void;
  valueAsHex: ColorPickerProviderProps['value'];
  valueAsHsv: Hsv;
};

const ColorPickerContext = createContext<ColorPickerContextValue | null>(null);

export type ColorPickerProviderProps = Required<ColorPickerProps>;

export const ColorPickerProvider = ({
  children,
  colorForComparison,
  minimumContrastRatio,
  onValueChange,
  opacityForContrastCalculation,
  value: valueAsHex,
}: ColorPickerProviderProps): JSX.Element => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [nonDerivedValueAsHsv, setNonDerivedValueAsHsv] = useState(convertToHsv(valueAsHex));
  const [valueAsHsv, setValueAsHsv] = useState(convertToHsv(valueAsHex));
  const [shouldEnforceMinContrast, setShouldEnforceMinContrast] = useState(false);

  const getAccessibleDerivativeColor = useCallback<
    ColorPickerContextValue['getAccessibleDerivativeColor']
  >(
    (originalColor) =>
      getAccessibleColor({
        originalColor,
        colorForComparison,
        minContrast: minimumContrastRatio,
        opacityForContrastCalculation,
      }),
    [colorForComparison, minimumContrastRatio, opacityForContrastCalculation],
  );

  const currentContrastRatio = calculateContrast({
    backgroundColor: valueAsHex,
    foregroundColor: colorForComparison,
    backgroundOpacity: opacityForContrastCalculation,
  });

  const hasAccessibleDerivativesValue = useMemo(() => {
    return hasAccessibleDerivatives({
      originalColor: valueAsHex,
      colorForComparison,
      minContrast: minimumContrastRatio,
      opacityForContrastCalculation,
    });
  }, [valueAsHex, colorForComparison, minimumContrastRatio, opacityForContrastCalculation]);

  const onChangeNonDerivedValueAsHsv = useCallback<
    ColorPickerContextValue['onChangeNonDerivedValueAsHsv']
  >(
    (nonDerivedValue) => {
      const finalValue = shouldEnforceMinContrast
        ? getAccessibleDerivativeColor(nonDerivedValue)
        : nonDerivedValue;
      const finalValueAsHex = formatHex(finalValue);

      if (finalValueAsHex !== valueAsHex) {
        onValueChange(finalValueAsHex);
      }

      setValueAsHsv(finalValue);
      setNonDerivedValueAsHsv(nonDerivedValue);
    },
    [getAccessibleDerivativeColor, onValueChange, shouldEnforceMinContrast, valueAsHex],
  );

  const changeValueSmoothly = useCallback(
    (toHsv: Hsv) => {
      setIsTransitioning(true);

      transitionBetweenColors(
        valueAsHsv,
        toHsv,
        (colorForStepAsHsv: Hsv) => {
          const hexValue = formatHex(colorForStepAsHsv);
          onValueChange(hexValue);
          setValueAsHsv(colorForStepAsHsv);
        },
        () => {
          setIsTransitioning(false);
        },
      );
    },
    [valueAsHsv, onValueChange],
  );

  const setShouldEnforceMinContrastWrapped = useCallback<
    ColorPickerContextValue['setShouldEnforceMinContrast']
  >(
    (newShouldEnforceMinContrast) => {
      setShouldEnforceMinContrast(newShouldEnforceMinContrast);

      const newColorAsHsv = newShouldEnforceMinContrast
        ? getAccessibleDerivativeColor(valueAsHex)
        : nonDerivedValueAsHsv;

      // Only transition if the color is actually different
      const currentHsv = valueAsHsv;
      if (
        currentHsv.h !== newColorAsHsv.h ||
        currentHsv.s !== newColorAsHsv.s ||
        currentHsv.v !== newColorAsHsv.v
      ) {
        changeValueSmoothly(newColorAsHsv);
      }
    },
    [
      changeValueSmoothly,
      getAccessibleDerivativeColor,
      nonDerivedValueAsHsv,
      valueAsHex,
      valueAsHsv,
    ],
  );

  const accessibleHsvFromValueAsHex = getAccessibleDerivativeColor(valueAsHex);
  const whatValueAsHexShouldBe = shouldEnforceMinContrast
    ? formatHex(accessibleHsvFromValueAsHex)
    : valueAsHex;
  const valueAsHsvConvertedToHex = formatHex(valueAsHsv);

  // This is to ensure that if the value prop is changed externally (i.e. not
  // via some interaction in ColorPicker), we update the internal state
  // accordingly
  if (whatValueAsHexShouldBe !== valueAsHsvConvertedToHex && !isTransitioning && !isDragging) {
    setValueAsHsv(
      shouldEnforceMinContrast
        ? getAccessibleDerivativeColor(valueAsHex)
        : convertToHsv(valueAsHex),
    );
    setNonDerivedValueAsHsv(convertToHsv(valueAsHex));
  }

  // If the value prop is set externally to a color that does not meet the
  // minimum contrast ratio, and contrast enforcement is enabled, we must update
  // that value to a derivative that does meet the ratio.
  useEffect(() => {
    // Yes, this is the exact same condition as above (outside of the useEffect)
    // but this one needs to be in a useEffect because calling onValueChange
    // causes a render _outside of_ this component (i.e. in its parent), which
    // you can't do during render.
    if (valueAsHex !== valueAsHsvConvertedToHex && !isTransitioning && !isDragging) {
      onValueChange(whatValueAsHexShouldBe);
    }
  }, [
    isDragging,
    isTransitioning,
    onValueChange,
    valueAsHex,
    valueAsHsvConvertedToHex,
    whatValueAsHexShouldBe,
  ]);

  const contextValue: ColorPickerContextValue = useMemo(
    () => ({
      colorForComparison,
      currentContrastRatio,
      getAccessibleDerivativeColor,
      hasAccessibleDerivatives: hasAccessibleDerivativesValue,
      isDragging,
      isTransitioning,
      minimumContrastRatio,
      nonDerivedValueAsHex: formatHex(nonDerivedValueAsHsv),
      nonDerivedValueAsHsv,
      onChangeNonDerivedValueAsHsv,
      opacityForContrastCalculation,
      setIsDragging,
      setShouldEnforceMinContrast: setShouldEnforceMinContrastWrapped,
      shouldEnforceMinContrast,
      valueAsHex,
      valueAsHsv,
    }),
    [
      colorForComparison,
      currentContrastRatio,
      getAccessibleDerivativeColor,
      hasAccessibleDerivativesValue,
      isDragging,
      isTransitioning,
      minimumContrastRatio,
      nonDerivedValueAsHsv,
      onChangeNonDerivedValueAsHsv,
      opacityForContrastCalculation,
      setShouldEnforceMinContrastWrapped,
      shouldEnforceMinContrast,
      valueAsHex,
      valueAsHsv,
    ],
  );

  return <ColorPickerContext.Provider value={contextValue}>{children}</ColorPickerContext.Provider>;
};

export const useColorPickerState = (): ColorPickerContextValue => {
  const context = useContext(ColorPickerContext);
  if (!context) {
    throw new Error('useColorPicker must be used within a ColorPickerProvider');
  }
  return context;
};
